/**
 * 내 결재함 API 클라이언트 — backend /approvals/* 와 1:1 (v1.5.0).
 */

import type {
  ApiResponse,
  ReportApprovalInfo,
  ReportApprovalListRow,
  ReportApprovalStatus,
} from './types';

const BASE = '/api';

export async function listReportApprovals(params: {
  status?: ReportApprovalStatus;
  q?: string;
  mine?: boolean;
  /** true = 최종승인 제외(진행 중 결재만) — 결재함 기본 화면 */
  notFinal?: boolean;
} = {}): Promise<{
  rows: ReportApprovalListRow[];
  counts: Record<string, number>;
  mineCount: number;
}> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.mine) qs.set('mine', '1');
  if (params.notFinal) qs.set('notFinal', '1');
  const res = await fetch(`${BASE}/approvals/sales-reports?${qs.toString()}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{
    rows: ReportApprovalListRow[];
    counts: Record<string, number>;
    mineCount: number;
  }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 판보 삭제 — 검토대기(최초 작성) 상태만, 작성자·admin */
export async function deleteReportApproval(salesId: string): Promise<void> {
  const res = await fetch(`${BASE}/approvals/sales-reports/${encodeURIComponent(salesId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ salesId: string; deleted: boolean }>;
  if (!json.ok) throw new Error(json.error.message);
}

export async function getReportApproval(salesId: string): Promise<ReportApprovalInfo> {
  const res = await fetch(`${BASE}/approvals/sales-reports/${encodeURIComponent(salesId)}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<ReportApprovalInfo>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

async function postAction(
  salesId: string,
  action: 'approve' | 'reject' | 'recall',
  body?: { reason?: string },
): Promise<{ salesId: string; status: ReportApprovalStatus; regenerated: boolean }> {
  const res = await fetch(
    `${BASE}/approvals/sales-reports/${encodeURIComponent(salesId)}/${action}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    },
  );
  const json = (await res.json()) as ApiResponse<{
    salesId: string;
    status: ReportApprovalStatus;
    regenerated: boolean;
  }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export const approveReport = (salesId: string) => postAction(salesId, 'approve');
export const rejectReport = (salesId: string, reason?: string) =>
  postAction(salesId, 'reject', { reason });
export const recallReport = (salesId: string) => postAction(salesId, 'recall');

/** 결재 배지 — count 는 합계(옛 호환), salesReport=판보 몫, expense=사내결재 몫 (v2.127.0) */
export type PendingCounts = { count: number; salesReport: number; expense: number };

export async function getPendingCounts(): Promise<PendingCounts> {
  const res = await fetch(`${BASE}/approvals/pending-count`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<Partial<PendingCounts> & { count: number }>;
  if (!json.ok) throw new Error(json.error.message);
  const d = json.data;
  // 옛 서버(두 숫자 없음)와도 맞물리게 — 합계만 있으면 전부 사내결재 몫으로 본다
  return { count: d.count, salesReport: d.salesReport ?? 0, expense: d.expense ?? d.count };
}

/** 결재판 서명 이미지 URL (있으면 <img> 로 렌더, 404 면 onError 로 숨김) */
export function signatureImageUrl(userId: string): string {
  return `${BASE}/auth/users/${encodeURIComponent(userId)}/signature`;
}
